import type { SavedContent } from '../../types';
import {
  GuideCard,
  ChallengeCard,
  ScormCard,
  PathCard,
  ResourceCard,
  EventSessionCard,
  FolderCard,
  DevelopmentContentCard,
  ContentProviderCard,
} from '~/elc/features/content-cards';
import { CmsFileCard, CmsLinkCard } from '~/elc/features/cms';
import Urls from '~/urls';
import { APP_CONSTANTS, I18n } from '~/common/explicit-exports';
import { sanitizeUserInput } from '~/utils/sanitize-user-input';
import { capitalizeWords } from '~/utils';
import {
  previewChallenge,
  previewGuide,
  startOrContinueChallenge,
  startOrContinueGuide,
  startOrContinuePath,
  startOrContinueSCORM,
  viewCMSFile,
  viewCMSLink,
  viewDevelopmentContent,
  viewLibraryFolder,
  viewResource,
} from '../../utils';
import { BookmarkButtonContainer } from '../BookmarkButton';

export interface SavedContentCardProps {
  content: SavedContent;
}

export function SavedContentCard({ content }: SavedContentCardProps) {
  const { contentType, content: savedContent } = content;

  const VIEW_DETAILS = I18n.t('js.learner.development_content.common.view_details');

  const mapTags = (tags: string[]) => tags.map((name) => ({ name }));

  if (contentType === 'guide') {
    const guide = savedContent;
    return (
      <GuideCard
        name={guide.displayTitle}
        description={sanitizeUserInput(guide.description ?? '')}
        imageSrc={guide.cardBanner}
        duration={guide.timeEstimateSeconds}
        progressTasks={guide.assignments?.[0]?.numCompletedTasks}
        totalTasks={guide.numTotalTasks}
        onStart={() => startOrContinueGuide(guide.id, guide.assignments?.[0]?.id)}
        onPreview={guide.previewEnabled ? () => previewGuide(guide.id) : undefined}
        tags={mapTags(guide.objectTags.map((t) => t.name) ?? [])}
        skills={guide.skills.map(({ skillName }) => skillName)}
        assignmentStatus={guide.assignments?.[0]?.assignmentStatus}
        actions={<BookmarkButtonContainer contentId={guide.id} contentType={contentType} />}
      />
    );
  }

  if (contentType === 'challenge') {
    const challenge = savedContent;
    return (
      <ChallengeCard
        name={challenge.displayTitle}
        description={sanitizeUserInput(challenge.description ?? '')}
        assignmentStatus={challenge.challengeUserAssignments?.[0]?.assignmentStatus}
        imageSrc={challenge.cardBanner}
        onStart={() => startOrContinueChallenge(challenge.id, challenge.challengeUserAssignments?.[0]?.id)}
        onPreview={challenge.previewEnabled ? () => previewChallenge(challenge.id) : undefined}
        tags={mapTags(challenge.objectTags.map((t) => t.name) ?? [])}
        skills={challenge.skills.map(({ skillName }) => skillName)}
        duration={challenge.timeEstimateSeconds}
        actions={<BookmarkButtonContainer contentId={challenge.id} contentType={contentType} />}
      />
    );
  }

  if (contentType === 'scorm') {
    const scorm = savedContent;
    if (
      savedContent.contentSource === APP_CONSTANTS.TOP_LEVEL_CONTENT_CONTENT_SOURCES.UDEMY ||
      savedContent.contentSource === APP_CONSTANTS.TOP_LEVEL_CONTENT_CONTENT_SOURCES.LINKEDIN_LEARNING
    ) {
      return (
        <ContentProviderCard
          name={scorm.displayTitle}
          description={sanitizeUserInput(scorm.description ?? '')}
          imageSrc={scorm.cardBanner}
          onStart={() => window.open(scorm.contentProviderContent.contentLink, '_blank')}
          assignmentStatus={scorm.assignments?.[0]?.assignmentStatus}
          tags={mapTags(scorm.objectTags.map((t) => t.name) ?? [])}
          skills={scorm.skills.map(({ skillName }) => skillName)}
          duration={scorm.timeEstimateSeconds}
          actions={<BookmarkButtonContainer contentId={scorm.id} contentType={contentType} />}
          contentSource={savedContent.contentSource}
        />
      );
    }
    return (
      <ScormCard
        name={scorm.displayTitle}
        description={sanitizeUserInput(scorm.description ?? '')}
        imageSrc={scorm.cardBanner}
        onStart={() => startOrContinueSCORM(scorm.id)}
        assignmentStatus={scorm.assignments?.[0]?.assignmentStatus}
        tags={mapTags(scorm.objectTags.map((t) => t.name) ?? [])}
        skills={scorm.skills.map(({ skillName }) => skillName)}
        duration={scorm.timeEstimateSeconds}
        actions={<BookmarkButtonContainer contentId={scorm.id} contentType={contentType} />}
      />
    );
  }

  if (contentType === 'training_series') {
    const path = savedContent;
    return (
      <PathCard
        name={path.displayTitle}
        description={sanitizeUserInput(path.description ?? '')}
        imageSrc={path.cardBanner}
        progress={path.trainingSeriesUserAssignments?.[0]?.modulesCompleted}
        assignmentStatus={path.trainingSeriesUserAssignments?.[0]?.assignmentStatus}
        total={path.trainingSeriesUserAssignments?.[0]?.numModules || path.numAssets}
        duration={path.timeEstimateSeconds}
        onStart={() => startOrContinuePath(savedContent, path.trainingClassSessionAttendeesByEventId)}
        tags={mapTags(path.objectTags.map((t) => t.name) ?? [])}
        skills={path.skills.map(({ skillName }) => skillName)}
        actions={<BookmarkButtonContainer contentId={path.id} contentType={contentType} />}
      />
    );
  }

  if (contentType === 'resource') {
    const resource = savedContent;
    return (
      <ResourceCard
        name={resource.displayTitle}
        description={sanitizeUserInput(resource.description ?? '')}
        imageSrc={resource.cardBanner}
        viewed={resource.userHasViewed}
        onPress={() => viewResource(resource.id)}
        tags={mapTags(resource.objectTags.map((t) => t.name) ?? [])}
        skills={resource.skills.map(({ skillName }) => skillName)}
        actions={<BookmarkButtonContainer contentId={resource.id} contentType={contentType} />}
      />
    );
  }

  if (contentType === 'training_class_session') {
    const session = savedContent;
    const { trainingClass: event } = session;
    return (
      <EventSessionCard
        isVirtual={!!session.zoomMeetingId || !!session.teamsMeetingId}
        title={session.displayTitle}
        subtitle={event.displayTitle}
        description={sanitizeUserInput(event.description ?? '')}
        imgUrl={event.cardBanner}
        tags={event.objectTags.map(({ name }) => name)}
        skills={event.skills.map(({ skillName }) => skillName)}
        startTime={session.start}
        endTime={session.end}
        location={session.location}
        numAttendees={session.numAttendees}
        href={Urls.eventOverview(event.id, null, session.id)}
        actions={<BookmarkButtonContainer contentId={session.id} contentType={contentType} />}
      />
    );
  }

  if (contentType === 'university_folder') {
    const folder = savedContent;
    return (
      <FolderCard
        name={folder.name}
        imageSrc={folder.cardBanner}
        description={sanitizeUserInput(folder.description ?? '')}
        numItems={folder.numItems ?? 0}
        onPress={() => viewLibraryFolder(folder.universityId, folder.id)}
        actions={<BookmarkButtonContainer contentId={folder.id} contentType={contentType} />}
      />
    );
  }

  if (contentType === 'go1_content') {
    const go1Content = savedContent;
    return (
      <DevelopmentContentCard
        title={go1Content.displayTitle}
        subtitle={capitalizeWords(go1Content.type)}
        description={sanitizeUserInput(go1Content.description ?? '')}
        actionText={VIEW_DETAILS}
        imgUrl={go1Content.cardBanner}
        estimatedTime={go1Content.timeEstimateSeconds}
        onPress={() => viewDevelopmentContent(go1Content.id)}
        actions={<BookmarkButtonContainer contentId={go1Content.id} contentType={contentType} />}
      />
    );
  }

  if (contentType === 'cms_file') {
    const handleAuditDataUpdated = async () => {
      // we don't want to go all the way back to the server, so this number may be wrong,
      // but this is better than not updating at all.
      cmsFile.metrics.totalDownloads += 1;
    };

    const cmsFile = savedContent;
    return (
      <CmsFileCard
        assetId={cmsFile.id}
        topLevelContentId={cmsFile.topLevelContent.id}
        name={cmsFile.topLevelContent.displayTitle}
        description={sanitizeUserInput(cmsFile.topLevelContent.description ?? '')}
        imageSrc={cmsFile.topLevelContent.cardBanner}
        onPress={() => viewCMSFile(cmsFile.id)}
        tags={mapTags(cmsFile.topLevelContent.objectTags.map((t) => t.name) ?? [])}
        metrics={cmsFile.metrics}
        fileType={cmsFile.fileType}
        actions={<BookmarkButtonContainer contentId={cmsFile.id} contentType={contentType} />}
        onAuditDataUpdated={handleAuditDataUpdated}
        allowExternalSharing={cmsFile.allowExternalSharing}
        allowInternalSharing={cmsFile.allowInternalSharing}
      />
    );
  }

  if (contentType === 'cms_link' && savedContent.url) {
    const handleAuditDataUpdated = async () => {
      // we don't want to go all the way back to the server, so this number may be wrong,
      // but this is better than not updating at all.
      cmsLink.metrics.totalDownloads += 1;
    };

    const cmsLink = savedContent;
    return (
      <CmsLinkCard
        assetId={cmsLink.id}
        topLevelContentId={cmsLink.topLevelContentId}
        name={cmsLink.topLevelContent.displayTitle}
        description={sanitizeUserInput(cmsLink.topLevelContent.description ?? '')}
        imageSrc={cmsLink.topLevelContent.cardBanner}
        onPress={() => viewCMSLink(cmsLink.id)}
        tags={mapTags(cmsLink.topLevelContent.objectTags.map((t) => t.name) ?? [])}
        metrics={cmsLink.metrics}
        url={cmsLink.url}
        allowExternalSharing={cmsLink.allowExternalSharing}
        actions={<BookmarkButtonContainer contentId={cmsLink.id} contentType={contentType} />}
        onAuditDataUpdated={handleAuditDataUpdated}
      />
    );
  }

  throw new Error(`Unsupported content type: ${contentType}`);
}
